import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  isoToYmd,
  normalizeOutgoingDate,
  todayYmd,
  hasOverdueInTree,
} from "../utils/date.ts";
// import { sendTodoNotification } from "../utils/notification.ts"; // ◀ 削除
import type {
  Task,
  TodoList as BaseTodoList,
  User,
  Tag,
  FilterState,
  RoomStatus,
  Reminder,
  CustomColumn,
  CustomColumnValue,
} from "../types/types";

export type TodoList = BaseTodoList & { status: string };

export interface SubTaskCreateDto {
  title: string;
  assigneeHonbuIds: number[];
  requesterHonbuIds: number[];
  startDate?: string;
  endDate?: string;
  memo?: string;
  customColumnValues?: { customColumnId: number; value: string }[];
  childTasks?: SubTaskCreateDto[];
}

export interface TaskCreateDto {
  todoListId: number;
  title: string;
  tagIds: number[];
  startDate?: string;
  endDate?: string;
  memo?: string;
  notification: boolean;
  priority: Task["priority"];
  createdHonbuId: number;
  roomId: number;
  type: "single" | "multi";
  status?: Task["status"];
  assigneeHonbuIds?: number[];
  requesterHonbuIds?: number[];
  childTasks?: SubTaskCreateDto[];
  /** F017: 既存添付ファイルへのリンク（テンプレート適用時に使用） */
  fileIds?: string[];
}

export interface TaskUpdateDto {
  title: string;
  tagIds: number[];
  startDate?: string;
  endDate?: string;
  memo?: string;
  notification: boolean;
  priority: Task["priority"];
  type: "single" | "multi";
  assigneeHonbuIds: number[];
  requesterHonbuIds: number[];
}

export interface TodoListCreateDto {
  name: string;
  roomId: number;
  createdHonbuId: number;
  templateId?: number;
}

// 送信DTOのホワイトリストサニタイズ
const allowedStatuses = new Set(["未対応", "対応中", "待機", "完了", "非表示"]);
function toNum(n: any): number {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}
function toBool(b: any): boolean {
  return !!b;
}
function toStr(s: any): string {
  return typeof s === "string" ? s : s == null ? "" : String(s);
}
function sanitizeNumberArray(arr: any): number[] {
  return Array.isArray(arr)
    ? arr
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n))
        .map((n) => n as number)
    : [];
}
// F017: fileId（GUID）配列のサニタイズ
function sanitizeGuidArray(arr: any): string[] {
  return Array.isArray(arr)
    ? arr
        .map((x) => String(x))
        .filter((s) => /^[0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$/.test(s))
    : [];
}
function sanitizeSubTasks(arr: any): SubTaskCreateDto[] {
  return Array.isArray(arr)
    ? arr.map((t: any) => ({
        title: toStr(t?.title),
        assigneeHonbuIds: sanitizeNumberArray(t?.assigneeHonbuIds),
        requesterHonbuIds: sanitizeNumberArray(t?.requesterHonbuIds),
        startDate: normalizeOutgoingDate(toStr(t?.startDate)) || undefined,
        endDate: normalizeOutgoingDate(toStr(t?.endDate)) || undefined,
        memo: toStr(t?.memo) || undefined,
        customColumnValues: Array.isArray(t?.customColumnValues)
          ? t.customColumnValues
              .map((v: any) => ({
                customColumnId: toNum(v?.customColumnId),
                value: toStr(v?.value),
              }))
              .filter(
                (v: { customColumnId: number; value: string }) =>
                  v.customColumnId !== 0 && v.value !== "",
              )
          : undefined,
        childTasks: sanitizeSubTasks(t?.childTasks),
      }))
    : [];
}
function sanitizeTaskCreateDto(dto: any): TaskCreateDto {
  const statusStr = toStr(dto?.status);
  const startOut = normalizeOutgoingDate(toStr(dto?.startDate));
  const endOut = normalizeOutgoingDate(toStr(dto?.endDate));
  return {
    todoListId: toNum(dto?.todoListId),
    title: toStr(dto?.title),
    tagIds: sanitizeNumberArray(dto?.tagIds),
    startDate: startOut,
    endDate: endOut,
    memo: toStr(dto?.memo) || undefined,
    notification: toBool(dto?.notification),
    priority: (dto?.priority ?? "なし") as Task["priority"],
    createdHonbuId: toNum(dto?.createdHonbuId),
    roomId: toNum(dto?.roomId),
    type: dto?.type === "multi" ? "multi" : "single",
    status: allowedStatuses.has(statusStr)
      ? (statusStr as Task["status"])
      : undefined,
    assigneeHonbuIds: sanitizeNumberArray(dto?.assigneeHonbuIds),
    requesterHonbuIds: sanitizeNumberArray(dto?.requesterHonbuIds),
    childTasks: sanitizeSubTasks(dto?.childTasks),
    fileIds: sanitizeGuidArray(dto?.fileIds),
  };
}
function sanitizeTaskUpdateDto(dto: any): TaskUpdateDto {
  const startOut = normalizeOutgoingDate(toStr(dto?.startDate));
  const endOut = normalizeOutgoingDate(toStr(dto?.endDate));
  return {
    title: toStr(dto?.title),
    tagIds: sanitizeNumberArray(dto?.tagIds),
    startDate: startOut,
    endDate: endOut,
    memo: toStr(dto?.memo) || undefined,
    notification: toBool(dto?.notification),
    priority: (dto?.priority ?? "なし") as Task["priority"],
    type: dto?.type === "multi" ? "multi" : "single",
    assigneeHonbuIds: sanitizeNumberArray(dto?.assigneeHonbuIds),
    requesterHonbuIds: sanitizeNumberArray(dto?.requesterHonbuIds),
  };
}

export function useTodos(
  roomId: number,
  honbuId: number,
  filters: FilterState,
  roomStatuses: RoomStatus[]
) {
  const [todoLists, setTodoLists] = useState<TodoList[]>([]);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customColumnValues, setCustomColumnValues] = useState<CustomColumnValue[]>([]);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [forceVisibleIds, setForceVisibleIds] = useState<Set<string>>(
    new Set()
  );
  const creatingDefaultRef = useRef(false);
  const transformRawTaskToTask = useCallback(
    (rawTask: any): Task => {
      const childTasks = (rawTask.childTasks || []).map((child: any) =>
        transformRawTaskToTask(child)
      );

      const finalStatus =
        roomStatuses.length > 0 &&
        roomStatuses.some((s) => s.statusName === rawTask.status)
          ? rawTask.status
          : "未対応";

      let assigneesRaw = rawTask.taskAssignees || [];
      if (roomId === 0) {
        assigneesRaw = assigneesRaw.filter(
          (a: any) => a.assigneeHonbuId === honbuId
        );
      }

      const recurrenceRules = (rawTask.recurrenceRules || []).map((r: any) => ({
        recurringRuleId: r.recurringRuleId,
        recurrenceType: r.recurrenceType,
        recurrenceValue: r.recurrenceValue,
        intervalMonths: r.intervalMonths ?? 1,
        recurrenceMonth: r.recurrenceMonth,
        periodStartDate: r.periodStartDate,
        periodEndDate: r.periodEndDate,
        dueDateType: r.dueDateType,
        dueDateRelativeDays: r.dueDateRelativeDays,
        dueDateNextDayOfMonth: r.dueDateNextDayOfMonth,
        isDueDateDuration: r.isDueDateDuration,
        dueDateRelativeDaysEnd: r.dueDateRelativeDaysEnd,
        dueDateNextDayOfMonthEnd: r.dueDateNextDayOfMonthEnd,
        notificationEnabled: r.notificationEnabled,
        reminderType: r.reminderType,
        reminderRelativeDays: r.reminderRelativeDays,
        reminderNextDayOfMonth: r.reminderNextDayOfMonth,
        contents: r.contents,
      }));

      return {
        id: rawTask.taskId.toString(),
        type: rawTask.type,
        title: rawTask.title,
        completed: rawTask.completed,
        status: rawTask.status || "未対応",
        priority: rawTask.priority || "なし",
        startDate: rawTask.startDate || "",
        endDate: rawTask.endDate || "",
        createdAt: rawTask.createdAt || new Date(0).toISOString(),
        lastUpdateAt: rawTask.lastUpdateAt || new Date(0).toISOString(),
        assignees: assigneesRaw.map(
          (a: any): User => ({
            id: a.assigneeHonbuId.toString(),
            name: a.name,
          })
        ),
        requesters: (rawTask.taskRequesters || []).map(
          (r: any): User => ({
            id: r.requesterHonbuId.toString(),
            name: r.name,
          })
        ),
        tags: (rawTask.taskTags || []).map(
          (t: any): Tag => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "",
          })
        ),
        memo: rawTask.memo || "",
        notification: rawTask.notification,
        starred: rawTask.starred,
        reminders: (rawTask.reminders || []).map((r: any) => ({
          reminderId: r.reminderId.toString(),
          taskId: r.taskId.toString(),
          reminderDateTime: r.reminderDateTime,
          isAutoGenerated: r.isAutoGenerated ?? false,
        })),
        recurrenceRules: recurrenceRules,
        childTasks: childTasks,
        completedAt: rawTask.completedAt || null,
        completedBy: rawTask.completedBy
          ? {
              id: rawTask.completedBy.assigneeHonbuId.toString(),
              name: rawTask.completedBy.name,
            }
          : undefined,
        createdHonbuId: rawTask.createdHonbuId,
        sourceRecurringRuleId: rawTask.sourceRecurringRuleId,
        taskFiles: (rawTask.taskFiles || []).map((f: any) => ({
          fileId: f.fileId,
          fileName: f.fileName,
          fileSize: f.fileSize,
          contentType: f.contentType,
          createdBy: f.createdBy,
          createdAt: f.createdAt,
        })),
      };
    },
    [roomId, honbuId, roomStatuses]
  );

  const fetchTodoLists = useCallback(
    async (includeHidden = false, options?: { bypassCache?: boolean }) => {
      setIsLoading(true);
      setForceVisibleIds(new Set());
      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      const bypass = options?.bypassCache === true;

      try {
        // --- My List (roomId === 0) の場合の特別処理 ---
        if (roomId === 0) {
          const response = await fetch(
            `/api/todolists?roomId=0&honbuId=${honbuId}&includeHidden=${includeHidden}`
          );
          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);

          const rawDataResponse: { lists: any[]; lastUpdatedAt: string } =
            await response.json();
          const transformedData: TodoList[] = (rawDataResponse.lists || []).map(
            (list: any) => ({
              id: list.todoListId.toString(),
              name: list.name,
              status: list.status,
              tasks: (list.tasks || []).map(transformRawTaskToTask),
            })
          );

          setTodoLists(transformedData);
          return; // My Listの場合はここで処理終了
        }

        // bypass=falseの場合のみキャッシュを確認
        if (!bypass) {
          const lastUpdatedResponse = await fetch(
            `/api/tasks/last-updated?roomId=${roomId}&honbuId=${honbuId}`
          );
          if (lastUpdatedResponse.ok) {
            const serverLastUpdatedAt = await lastUpdatedResponse.json();
            const cachedLastUpdatedAt = localStorage.getItem(lastUpdatedKey);
            const cachedData = localStorage.getItem(cacheKey);

            if (
              cachedLastUpdatedAt &&
              serverLastUpdatedAt === cachedLastUpdatedAt &&
              cachedData
            ) {
              const parsedData = JSON.parse(cachedData);
              setTodoLists(parsedData);
              setIsLoading(false);
              return; // キャッシュが有効ならここで処理終了
            }
          }
        }

        // キャッシュが無効、またはbypass=trueの場合はサーバーからデータを取得
        const response = await fetch(
          `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=${includeHidden}`
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const rawDataResponse: { lists: any[]; lastUpdatedAt: string } =
          await response.json();
        const { lists: rawData, lastUpdatedAt: newLastUpdatedAt } =
          rawDataResponse;

        let transformedData: TodoList[] = rawData.map((list) => {
          const tasks: Task[] = (list.tasks || []).map(transformRawTaskToTask);
          return {
            id: list.todoListId.toString(),
            name: list.name,
            status: list.status,
            tasks: tasks,
          };
        });

        // ここで「表示できる（Active）プロジェクトが一つもない」場合も新規作成する
        const hasActive = transformedData.some((l) => l.status === "Active");

        if (!hasActive) {
          // 既に別の処理で作成中なら複数作成を防ぐ
          if (!creatingDefaultRef.current) {
            creatingDefaultRef.current = true;
            try {
              const createResp = await fetch("/api/todolists", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  name: "新規プロジェクト",
                  roomId: roomId,
                  createdHonbuId: honbuId,
                }),
              });
              if (createResp.ok) {
                // 作成後に改めて一覧を取得して transformedData を更新
                const reResp = await fetch(
                  `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=${includeHidden}`
                );
                if (reResp.ok) {
                  const reJson: { lists: any[]; lastUpdatedAt: string } =
                    await reResp.json();
                  const reRaw = reJson.lists || [];
                  transformedData = reRaw.map((list: any) => {
                    const tasks: Task[] = (list.tasks || []).map(
                      transformRawTaskToTask
                    );
                    return {
                      id: list.todoListId.toString(),
                      name: list.name,
                      status: list.status,
                      tasks: tasks,
                    };
                  });
                }
              } else {
                console.error(
                  "Failed to create default todo list:",
                  await createResp.text().catch(() => "")
                );
              }
            } catch (e) {
              console.error("Error creating default todo list:", e);
            } finally {
              creatingDefaultRef.current = false;
            }
          }
        }

        // キャッシュを更新
        if (newLastUpdatedAt) {
          localStorage.setItem(cacheKey, JSON.stringify(transformedData));
          localStorage.setItem(lastUpdatedKey, newLastUpdatedAt);
        }

        setTodoLists(transformedData);
      } catch (error) {
        console.error("Failed to fetch todo lists:", error);
        setTodoLists([]);
      } finally {
        setIsLoading(false);
      }
    },
    [roomId, honbuId, activeListId, transformRawTaskToTask]
  );

  useEffect(() => {
    fetchTodoLists();
  }, [fetchTodoLists]);

  const prevRoomIdRef = useRef<number | null>(null);
  const prevHonbuIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      prevRoomIdRef.current !== null &&
      (prevRoomIdRef.current !== roomId || prevHonbuIdRef.current !== honbuId)
    ) {
      setActiveListId(null);
      fetchTodoLists(false, { bypassCache: true });
    }
    prevRoomIdRef.current = roomId;
    prevHonbuIdRef.current = honbuId;
  }, [roomId, honbuId, fetchTodoLists]);

  // アクティブリストのカスタム列の値を取得する
  const fetchCustomColumnValues = useCallback(() => {
    if (!activeListId || activeListId === "0") {
      setCustomColumnValues([]);
      return;
    }
    fetch(`/api/todolists/${activeListId}/custom-column-values`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: CustomColumnValue[]) => setCustomColumnValues(data))
      .catch(() => setCustomColumnValues([]));
  }, [activeListId]);

  // アクティブリストが変わったらカスタム列の値を再取得
  useEffect(() => {
    fetchCustomColumnValues();
  }, [fetchCustomColumnValues]);

  // アクティブリストのカスタム列の定義を取得する
  const fetchCustomColumns = useCallback(() => {
    if (!activeListId || activeListId === "0") {
      setCustomColumns([]);
      return;
    }
    fetch(`/api/todolists/${activeListId}/custom-columns?honbuId=${honbuId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: CustomColumn[]) => setCustomColumns(data))
      .catch(() => setCustomColumns([]));
  }, [activeListId, honbuId]);

  // アクティブリストが変わったらカスタム列の定義を再取得
  useEffect(() => {
    fetchCustomColumns();
  }, [fetchCustomColumns]);

  const activeList = todoLists.find((list) => list.id === activeListId);

  const updateListName = async (
    listId: string,
    name: string
  ): Promise<boolean> => {
    try {
      const response = await fetch(`/api/todolists/${listId}/name`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!response.ok) {
        throw new Error("API request failed");
      }
      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(lastUpdatedKey);
      return true;
    } catch (error) {
      console.error("API call in updateListName failed.", error);
      return false;
    }
  };

  const filteredTasks = useMemo(() => {
    if (!activeList) return [];
    const tasksToFilter = activeList.tasks;

    const today = todayYmd();

    const toNumArr = (arr: unknown[] | undefined): number[] =>
      (arr ?? [])
        .map((x) => Number(x))
        .filter((n): n is number => !Number.isNaN(n));

    const filterAssigneeIds = toNumArr((filters as any).assigneeIds);
    const filterRequesterIds = toNumArr((filters as any).requesterIds);
    const filterTagIds = toNumArr((filters as any).tagIds);

    const filterStartYmd = filters.startDate ? isoToYmd(filters.startDate) : "";
    const filterEndYmd = filters.endDate ? isoToYmd(filters.endDate) : "";

    const searchKeyword = (filters.searchKeyword ?? "").trim().toLowerCase();
    const searchById = filters.searchMode === "id";
    const searchIdValue = (filters.searchKeyword ?? "").trim();

    const checkFilters = (task: Task): boolean => {
      // 一時表示リストに含まれていれば、フィルターを無視して表示する
      if (forceVisibleIds.has(task.id)) {
        return true;
      }

      // "Deleted" 状態のタスクは常に除外
      if ((task as any).status === "Deleted") {
        return false;
      }
      // "完了"タスクの表示制御
      if (task.status === "完了" && !filters.showCompleted) {
        return false;
      }
      // "非表示"タスクの表示制御
      if (task.status === "非表示" && !filters.showHidden) {
        return false;
      }
      if (
        filters.myTasksOnly &&
        !(task.assignees || []).some((a) => Number(a.id) === honbuId)
      )
        return false;
      if (
        filters.myRequestsOnly &&
        !(task.requesters || []).some((r) => Number(r.id) === honbuId)
      )
        return false;
      if (filters.starredOnly && !task.starred) return false;

      if (searchById) {
        // タスクID検索: メインタスク（親）のIDと完全一致するもののみ表示する
        if (searchIdValue && task.id !== searchIdValue) {
          return false;
        }
      } else {
        // 名前検索: 親タスク（メインタスク名）が一致するもののみ表示する
        if (
          searchKeyword &&
          !task.title.toLowerCase().includes(searchKeyword)
        ) {
          return false;
        }
      }

      // 自動作成タスクのみ表示
      if (
        (filters as any).recurringOnly &&
        (!task.recurrenceRules || task.recurrenceRules.length === 0)
      ) {
        return false;
      }

      if (filters.dueTodayOnly) {
        if (!task.startDate) return false;
        const startY = isoToYmd(task.startDate);
        const endY = isoToYmd(task.endDate || task.startDate);
        if (!(startY <= today && today <= endY)) return false;
      }
      // 期限切れのみ: 親が期限内でもサブタスクが期限切れなら放置対象として表示する
      if (filters.overdueOnly && !hasOverdueInTree(task, today)) {
        return false;
      }
      if (
        (filters.statuses?.length ?? 0) > 0 &&
        !filters.statuses.includes(task.status)
      )
        return false;
      if (
        (filters.priorities?.length ?? 0) > 0 &&
        !filters.priorities.includes(task.priority)
      )
        return false;

      if (filterStartYmd || filterEndYmd) {
        if (!task.startDate) {
          return false;
        }

        const taskStartYmd = isoToYmd(task.startDate);
        const taskEndYmd = isoToYmd(task.endDate || task.startDate);

        if (filterStartYmd && taskEndYmd < filterStartYmd) {
          return false;
        }
        if (filterEndYmd && taskStartYmd > filterEndYmd) {
          return false;
        }
      }

      if (filterAssigneeIds.length > 0) {
        const taskAssigneeIds = new Set(
          (task.assignees || []).map((a) => Number(a.id))
        );
        if ((filters as any).assigneeOperator === "AND") {
          if (!filterAssigneeIds.every((id) => taskAssigneeIds.has(id)))
            return false;
        } else {
          if (!filterAssigneeIds.some((id) => taskAssigneeIds.has(id)))
            return false;
        }
      }

      if (filterRequesterIds.length > 0) {
        const taskRequesterIds = new Set(
          (task.requesters || []).map((r) => Number(r.id))
        );
        if ((filters as any).requesterOperator === "AND") {
          if (!filterRequesterIds.every((id) => taskRequesterIds.has(id)))
            return false;
        } else {
          if (!filterRequesterIds.some((id) => taskRequesterIds.has(id)))
            return false;
        }
      }

      if (filterTagIds.length > 0) {
        const taskTagIds = new Set((task.tags || []).map((t) => Number(t.id)));
        if ((filters as any).tagOperator === "AND") {
          if (!filterTagIds.every((id) => taskTagIds.has(id))) return false;
        } else {
          if (!filterTagIds.some((id) => taskTagIds.has(id))) return false;
        }
      }

      // カスタム項目フィルター（同一columnId: OR、異なるcolumnId: AND）
      const customColumnConditions = (filters as any)
        .customColumnConditions as
        | Array<{ columnId: number; columnType: string; value: string }>
        | undefined;
      if (customColumnConditions && customColumnConditions.length > 0) {
        const taskIdNum = parseInt(task.id, 10);
        const byColumn = new Map<
          number,
          Array<{ type: string; value: string }>
        >();
        for (const c of customColumnConditions) {
          if (!c.value.trim()) continue;
          if (!byColumn.has(c.columnId)) byColumn.set(c.columnId, []);
          byColumn
            .get(c.columnId)!
            .push({ type: c.columnType, value: c.value.trim().toLowerCase() });
        }
        for (const [columnId, conditions] of byColumn.entries()) {
          const entry = customColumnValues.find(
            (cv) => cv.taskId === taskIdNum && cv.customColumnId === columnId
          );
          const actualValue = (entry?.value ?? "").toLowerCase();
          const matchesAny = conditions.some((c) =>
            c.type === "select"
              ? actualValue === c.value
              : actualValue.includes(c.value)
          );
          if (!matchesAny) return false;
        }
      }

      return true;
    };

    const recursiveFilter = (tasks: Task[], isSubtask = false): Task[] => {
      const result: Task[] = [];
      for (const task of tasks) {
        let passes = true;
        if (isSubtask) {
          if (task.status === "非表示") {
            passes = false;
          }
        } else {
          passes = checkFilters(task);
        }

        if (passes) {
          let filteredChildren: Task[] = [];
          if (task.childTasks && task.childTasks.length > 0) {
            filteredChildren = recursiveFilter(task.childTasks, true);
          }
          result.push({ ...task, childTasks: filteredChildren });
        }
      }
      return result;
    };
    return recursiveFilter(tasksToFilter);
  }, [activeList, filters, honbuId, forceVisibleIds, customColumnValues]);

  const updateTaskInState = (
    taskId: string,
    updateFn: (task: Task) => Task
  ) => {
    const updateRecursively = (tasks: Task[]): Task[] => {
      return tasks.map((task) => {
        if (task.id === taskId) {
          return updateFn(task);
        }
        if (task.childTasks && task.childTasks.length > 0) {
          return { ...task, childTasks: updateRecursively(task.childTasks) };
        }
        return task;
      });
    };
    setTodoLists((currentLists) =>
      currentLists.map((list) =>
        list.id === activeListId
          ? { ...list, tasks: updateRecursively(list.tasks) }
          : list
      )
    );
  };

  const toggleTaskComplete = async (taskId: string, completed: boolean) => {
    setForceVisibleIds((prev) => {
      const next = new Set(prev);
      if (completed) {
        next.add(taskId);
      } else {
        next.delete(taskId);
      }
      return next;
    });

    updateTaskInState(taskId, (task) => ({
      ...task,
      completed,
      status: completed ? "完了" : "未対応",
    }));

    try {
      const res = await fetch(`/api/tasks/${taskId}/complete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed, updatedByHonbuId: honbuId }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error(`toggleTaskComplete failed: ${res.status}`, txt);
        throw new Error(txt || `HTTP ${res.status}`);
      }
    } catch (error) {
      console.error("Failed to update task completion", error);
      fetchTodoLists();
    }
  };

  const updateTaskStatus = async (
    taskId: string,
    newStatus: Task["status"],
    options?: { keepVisible?: boolean }
  ) => {
    if (options?.keepVisible) {
      setForceVisibleIds((prev) => new Set(prev).add(taskId));
    }

    updateTaskInState(taskId, (task) => ({
      ...task,
      status: newStatus,
      completed: newStatus === "完了",
    }));

    try {
      const res = await fetch(`/api/tasks/${taskId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, updatedByHonbuId: honbuId }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        console.error(`updateTaskStatus failed: ${res.status}`, txt);
        throw new Error(txt || `HTTP ${res.status}`);
      }
    } catch (error) {
      console.error("Failed to update status", error);
      fetchTodoLists();
    }
  };

  const toggleTaskStar = async (taskId: string, starred: boolean) => {
    updateTaskInState(taskId, (task) => ({ ...task, starred }));
    try {
      await fetch(`/api/tasks/${taskId}/star`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ starred }),
      });
    } catch (error) {
      console.error("Failed to update star", error);
      fetchTodoLists();
    }
  };

  const addTodoList = async (listData: TodoListCreateDto) => {
    try {
      const response = await fetch("/api/todolists", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(listData),
      });
      if (!response.ok) throw new Error("Failed to create todo list");

      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(lastUpdatedKey);

      await fetchTodoLists(false, { bypassCache: true });
    } catch (error) {
      console.error("addTodoList error:", error);
    }
  };

  const addTask = async (
    taskData: TaskCreateDto,
    assigneeNames: string[]
  ): Promise<string | null> => {
    try {
      const payload = sanitizeTaskCreateDto(taskData);
      const response = await fetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to create task");
      const createdTask = await response.json();
      await fetchTodoLists();
      return createdTask.taskId?.toString() || null;
    } catch (error) {
      console.error(error);
      await fetchTodoLists();
      return null;
    }
  };

  const addBulkTasks = async (
    tasksWithAssignees: { task: TaskCreateDto; assigneeNames: string[] }[]
  ) => {
    try {
      const tasksData = tasksWithAssignees.map((item) => item.task);
      const payload = tasksData.map((d) => sanitizeTaskCreateDto(d));

      const response = await fetch("/api/tasks/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to create bulk tasks");
      await fetchTodoLists();
    } catch (error) {
      console.error(error);
    }
  };

  const updateTask = async (taskId: string, taskData: TaskUpdateDto) => {
    try {
      const payload = sanitizeTaskUpdateDto(taskData);
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to update task");
      await fetchTodoLists();
    } catch (error) {
      console.error(error);
    }
  };

  const updateRecurrenceContents = async (ruleId: number, contents: string): Promise<void> => {
    try {
      const response = await fetch(`/api/recurrence/${ruleId}/contents`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents }),
      });
      if (!response.ok) throw new Error("Failed to update recurrence contents");
    } catch (error) {
      console.error(error);
    }
  };

  const getTaskRecurrenceRule = async (taskId: string): Promise<number | null> => {
    try {
      const response = await fetch(`/api/tasks/${taskId}/recurrence-rule`);
      if (!response.ok) return null;
      const rule = await response.json();
      return rule?.recurringRuleId ?? null;
    } catch {
      return null;
    }
  };

  const toggleTaskNotification = async (
    taskId: string,
    notification: boolean
  ) => {
    updateTaskInState(taskId, (task) => ({ ...task, notification }));
    try {
      await fetch(`/api/tasks/${taskId}/notification`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notification }),
      });
    } catch (error) {
      console.error("Failed to update notification status", error);
      fetchTodoLists();
    }
  };

  const fetchAndMergeTasks = useCallback(
    async (status: "完了" | "非表示") => {
      if (!activeListId) return;
      try {
        const response = await fetch(
          `/api/tasks/by-status?roomId=${roomId}&honbuId=${honbuId}&status=${encodeURIComponent(
            status
          )}&todoListId=${activeListId}`
        );
        if (!response.ok) throw new Error(`Failed to fetch ${status} tasks`);
        const rawTasks = await response.json();
        const newTasks = rawTasks.map(transformRawTaskToTask);

        setTodoLists((currentLists) => {
          return currentLists.map((list) => {
            if (list.id === activeListId) {
              const existingTaskIds = new Set(list.tasks.map((t) => t.id));
              const tasksToAdd = newTasks.filter(
                (nt: Task) => !existingTaskIds.has(nt.id)
              );
              return { ...list, tasks: [...list.tasks, ...tasksToAdd] };
            }
            return list;
          });
        });
      } catch (error) {
        console.error(error);
      }
    },
    [activeListId, roomId, honbuId, transformRawTaskToTask]
  );

  const updateListStatus = async (listId: string, status: string) => {
    try {
      const response = await fetch(`/api/todolists/${listId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(lastUpdatedKey);

      await fetchTodoLists(true);
    } catch (error) {
      console.error("Failed to update list status", error);
    }
  };

  const deleteList = async (listId: string) => {
    try {
      const response = await fetch(`/api/todolists/${listId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(lastUpdatedKey);

      await fetchTodoLists(true);
    } catch (error) {
      console.error("Failed to delete list", error);
    }
  };

  const hasAnyFilter =
    filters.myTasksOnly ||
    filters.myRequestsOnly ||
    filters.starredOnly ||
    filters.dueTodayOnly ||
    filters.overdueOnly ||
    (filters.statuses?.length ?? 0) > 0 ||
    (filters.priorities?.length ?? 0) > 0 ||
    !!filters.startDate ||
    !!filters.endDate ||
    (filters.assigneeIds?.length ?? 0) > 0 ||
    (filters.requesterIds?.length ?? 0) > 0 ||
    (filters.tagIds?.length ?? 0) > 0 ||
    !!filters.searchKeyword?.trim() ||
    ((filters as any).customColumnConditions?.length ?? 0) > 0;

  const deleteTask = async (taskId: string) => {
    // 楽観的更新のため、現在の状態をバックアップ
    const previousTodoLists = JSON.parse(JSON.stringify(todoLists));

    // UIから該当タスクを再帰的に削除するヘルパー関数
    const deleteTaskRecursively = (
      tasks: Task[],
      idToDelete: string
    ): Task[] => {
      return tasks
        .filter((task) => task.id !== idToDelete)
        .map((task) => {
          if (task.childTasks && task.childTasks.length > 0) {
            return {
              ...task,
              childTasks: deleteTaskRecursively(task.childTasks, idToDelete),
            };
          }
          return task;
        });
    };

    setTodoLists((currentLists) =>
      currentLists.map((list) => ({
        ...list,
        tasks: deleteTaskRecursively(list.tasks, taskId),
      }))
    );

    try {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete task on the server");
      }
      const cacheKey = `todoLists_${roomId}_${honbuId}`;
      const lastUpdatedKey = `lastUpdatedAt_${roomId}_${honbuId}`;
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(lastUpdatedKey);
    } catch (error) {
      console.error("Failed to delete task:", error);
      setTodoLists(previousTodoLists);
    }
  };

  const addReminder = async (
    taskId: string,
    reminderDateTime: Date
  ): Promise<Reminder | null> => {
    try {
      const response = await fetch(`/api/tasks/${taskId}/reminders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reminderDateTime: reminderDateTime.toISOString(),
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to add reminder");
      }
      const newReminderDto = await response.json();
      return {
        reminderId: newReminderDto.reminderId.toString(),
        taskId: newReminderDto.taskId.toString(),
        reminderDateTime: newReminderDto.reminderDateTime,
      };
    } catch (error) {
      console.error("Failed to add reminder:", error);
      return null;
    }
  };

  const deleteReminder = async (
    reminderId: string | number
  ): Promise<boolean> => {
    try {
      const response = await fetch(`/api/reminders/${reminderId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete reminder");
      }
      return true;
    } catch (error) {
      console.error("Failed to delete reminder:", error);
      return false;
    }
  };

  const forceReload = () => fetchTodoLists(false, { bypassCache: true });
  const addSubtask = async (parentTaskId: string, title: string): Promise<string | null> => {
    try {
      const response = await fetch(`/api/tasks/${parentTaskId}/subtasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) {
        throw new Error("Failed to add subtask");
      }
      const data = await response.json();
      await forceReload();
      return data.taskId?.toString() ?? null;
    } catch (error) {
      console.error("Error adding subtask:", error);
      return null;
    }
  };

  const reorderSubtasks = async (
    parentTaskId: string,
    subtaskIds: string[]
  ) => {
    // 楽観的UI更新：サーバーリクエストの前にローカル状態を更新
    const previousTodoLists = [...todoLists];

    try {
      // ローカル状態を先に更新
      setTodoLists((prevLists) =>
        prevLists.map((list) => ({
          ...list,
          tasks: list.tasks.map((task) => {
            if (task.id === parentTaskId && task.childTasks) {
              // サブタスクを新しい順序で並び替え
              const reorderedChildTasks = subtaskIds
                .map((id) => task.childTasks!.find((child) => child.id === id))
                .filter((child): child is Task => child !== undefined);

              return {
                ...task,
                childTasks: reorderedChildTasks,
              };
            } else if (task.childTasks && task.childTasks.length > 0) {
              // ネストされたサブタスク内も再帰的に探索
              const updateChildTasks = (tasks: Task[]): Task[] => {
                return tasks.map((t) => {
                  if (t.id === parentTaskId && t.childTasks) {
                    const reorderedChildTasks = subtaskIds
                      .map((id) =>
                        t.childTasks!.find((child) => child.id === id)
                      )
                      .filter((child): child is Task => child !== undefined);
                    return { ...t, childTasks: reorderedChildTasks };
                  } else if (t.childTasks && t.childTasks.length > 0) {
                    return { ...t, childTasks: updateChildTasks(t.childTasks) };
                  }
                  return t;
                });
              };

              return {
                ...task,
                childTasks: updateChildTasks(task.childTasks),
              };
            }
            return task;
          }),
        }))
      );

      const response = await fetch(
        `/api/tasks/${parentTaskId}/subtasks/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subtaskIds: subtaskIds.map((id) => parseInt(id, 10)),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reorder subtasks");
      }

      // サーバーから最新データを取得して同期
      await forceReload();
    } catch (error) {
      console.error("Error reordering subtasks:", error);
      // エラーが発生した場合は元の状態に戻す
      setTodoLists(previousTodoLists);
    }
  };

  return {
    todoLists,
    setTodoLists,
    activeList,
    activeListId,
    setActiveListId,
    filteredTasks,
    hasAnyFilter,
    toggleTaskComplete,
    updateTaskStatus,
    toggleTaskStar,
    addTodoList,
    addTask,
    addBulkTasks,
    updateTask,
    deleteTask,
    toggleTaskNotification,
    fetchAndMergeTasks,
    isLoading,
    updateListStatus,
    deleteList,
    fetchAllLists: () => fetchTodoLists(true, { bypassCache: true }),
    addReminder,
    deleteReminder,
    refreshTodos: () => fetchTodoLists(false),
    forceReload: () => fetchTodoLists(false, { bypassCache: true }),
    isMyListMode: roomId === 0,
    updateListName,
    addSubtask,
    reorderSubtasks,
    updateRecurrenceContents,
    getTaskRecurrenceRule,
    refreshCustomColumnValues: fetchCustomColumnValues,
    customColumns,
    customColumnValues,
    refreshCustomColumns: fetchCustomColumns,
  };
}
